import { useState } from "react";
import { ArrowLeft, ArrowUpRight, LockKeyhole } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { useAuth } from "@/auth/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const destination = location.state?.from || "/insights";
  const resetMessage = searchParams.get("reset") === "1" ? "Your password has been updated. Sign in with the new password." : "";

  if (user) return <Navigate to={destination} replace />;

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate(destination, { replace: true });
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="auth-page" data-testid="editor-login-page">
      <SeoHead title="Sign in | Ashwini Patil" description="Sign in to your Ashwini Patil account." canonical="https://ashwinipatil.ai/login" noIndex schemas={[]} />
      <div className="shell auth-shell">
        <Link to="/insights" className="inline-link" data-testid="login-back-link"><ArrowLeft size={15} /> Back to insights</Link>
        <div className="auth-card">
          <div className="auth-mark"><LockKeyhole size={19} /></div>
          <div className="section-kicker">Email &amp; password</div>
          <h1 className="auth-title">Return to the work.</h1>
          <p className="auth-copy">Sign in to continue to your account, private records, and any editorial workspace you can access.</p>
          <form onSubmit={submit} data-testid="editor-login-form">
            <label className="form-field"><span className="form-label">Email</span><input className="form-input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required data-testid="editor-login-email" /></label>
            <label className="form-field"><span className="form-label">Password</span><input className="form-input" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required data-testid="editor-login-password" /></label>
            <button type="submit" className="button-primary" disabled={sending} data-testid="editor-login-submit">{sending ? "Signing in…" : "Sign in"} <ArrowUpRight size={15} /></button>
            <p className="auth-error" role="alert" data-testid="editor-login-error">{error}</p>
            <p className="auth-success" role="status" data-testid="editor-login-message">{resetMessage}</p>
          </form>
          <div className="auth-link-row"><Link to="/forgot-password" data-testid="login-forgot-password-link">Forgot password?</Link><Link to="/register" data-testid="login-register-link">Create an account</Link></div>
        </div>
      </div>
    </main>
  );
}
